import {
  accountUserProfileService,
  type AccountUserProfileBirthSeason,
  type AccountUserProfileHoroscope,
  type AccountUserProfileRecord,
} from './accountUserProfileService';
import { inferSeasonFromHoroscope } from '../utils/horoscopeCatalog';

const profileByUserId = new Map<string, AccountUserProfileRecord>();
const hydrationByUserId = new Map<string, Promise<AccountUserProfileRecord | null>>();

export function buildBirthYearOptions(referenceDate = new Date()) {
  const currentYear = referenceDate.getFullYear();
  const years: number[] = [];
  for (let age = 3; age <= 80; age += 1) {
    years.push(currentYear - age);
  }
  return years;
}

export function needsDemographicsPrompt(profile: AccountUserProfileRecord | null | undefined) {
  if (!profile) {
    return true;
  }
  if (profile.birthYear == null || profile.birthSeason == null) {
    return true;
  }
  if ('horoscope' in profile && profile.horoscope == null) {
    return true;
  }
  return false;
}

export function getCachedDemographics(userId: string) {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    return null;
  }
  return profileByUserId.get(normalizedUserId) ?? null;
}

export async function hydrateDemographics(userId: string): Promise<AccountUserProfileRecord | null> {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    return null;
  }

  const pending = hydrationByUserId.get(normalizedUserId);
  if (pending) {
    return pending;
  }

  const task = (async () => {
    const bundle = await accountUserProfileService.getProfileBundle(normalizedUserId);
    profileByUserId.set(normalizedUserId, bundle.profile);
    return bundle.profile;
  })().catch((error) => {
    console.warn('[demographicsService] hydrateDemographics failed:', error);
    return null;
  }).finally(() => {
    hydrationByUserId.delete(normalizedUserId);
  });

  hydrationByUserId.set(normalizedUserId, task);
  return task;
}

export async function saveDemographics(
  userId: string,
  patch: {
    birthYear?: number;
    birthSeason?: AccountUserProfileBirthSeason;
    horoscope: AccountUserProfileHoroscope;
  },
) {
  const normalizedUserId = String(userId || '').trim();
  const resolvedSeason = patch.birthSeason ?? inferSeasonFromHoroscope(patch.horoscope);

  const updated = await accountUserProfileService.patchDemographics(normalizedUserId, {
    ...patch,
    birthSeason: resolvedSeason,
  });
  profileByUserId.set(normalizedUserId, updated);
  return updated;
}

export function clearDemographicsCache(userId?: string) {
  if (!userId) {
    profileByUserId.clear();
    return;
  }
  profileByUserId.delete(String(userId).trim());
}
